import { MagnifyingGlassIcon, MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { logout } from "../../auth/authSlice";
import type { RootState } from "../../redux/store";
import { logout as logoutApi, getDeviceIdentifier } from "../../services/authService";
import { toggleTheme } from "../../theme/themeSlice";

import { NavbarNotificationsMenu } from "./NavbarNotificationsMenu";
import { NavbarProfileMenu } from "./NavbarProfileMenu";
import { WorkspaceAcademicYear } from "./WorkspaceAcademicYear";
import { WorkspaceTabs } from "./WorkspaceTabs";

import { useNavigationPersona } from "@/auth/navigationPersona";
import { Button } from "@/components/Button";

interface TopNavbarProps {
  onSidebarToggle: () => void;
  onMobileMenuToggle: () => void;
  sidebarCollapsed: boolean;
}

export function TopNavbar({
  onSidebarToggle,
  onMobileMenuToggle,
  sidebarCollapsed,
}: TopNavbarProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme, user, unreadCount } = useSelector(
    (state: RootState) => ({
      theme: state.theme?.theme ?? "light",
      user: state.auth?.user ?? null,
      unreadCount: state.notifications?.unreadCount ?? 0,
    }),
    shallowEqual,
  );
  const navigationPersona = useNavigationPersona();

  const handleThemeToggle = () => dispatch(toggleTheme());

  const handleSignOut = async () => {
    await logoutApi(getDeviceIdentifier());
    dispatch(logout());
    navigate("/login");
  };

  return (
    <header className="sticky top-[var(--platform-banner-h,0px)] z-40 border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:bg-white focus:p-2 dark:focus:bg-slate-900"
      >
        Skip to content
      </a>
      <div className="flex h-11 min-w-0 items-center justify-between gap-1 px-2 sm:gap-2 sm:px-3">
        {/* Left: Menu + Search */}
        <div className="flex min-w-0 flex-1 items-center gap-1 self-stretch">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onMobileMenuToggle}
            aria-label="Open navigation"
            className="!p-1.5 lg:hidden"
          >
            <PanelLeftOpen className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onSidebarToggle}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden !p-1.5 lg:flex"
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </Button>
          <WorkspaceTabs
            key={`${user?.tenantId ?? user?.schoolId}.${user?.id}.${user?.activeContext}`}
          />
        </div>

        {/* Right: Academic year + Theme + Notifications + Profile */}
        <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
          {navigationPersona !== "guardian" && (
            <button
              type="button"
              className="shell-icon"
              aria-label="Search modules (Ctrl or Command K)"
              title="Search modules · Ctrl/Cmd + K"
              onClick={() => window.dispatchEvent(new Event("sms:open-search"))}
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </button>
          )}
          <WorkspaceAcademicYear />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleThemeToggle}
            aria-label="Toggle theme"
            className="!p-1.5"
          >
            {theme === "light" ? (
              <MoonIcon className="w-4 h-4" />
            ) : (
              <SunIcon className="w-4 h-4" />
            )}
          </Button>

          <NavbarNotificationsMenu
            key={`${user?.tenantId ?? user?.schoolId}.${user?.id}.${user?.activeContext}`}
            unreadCount={unreadCount}
          />
          <NavbarProfileMenu user={user} onSignOut={handleSignOut} />
        </div>
      </div>
    </header>
  );
}
